import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, IconButton, Divider, Autocomplete, TextField,
  Chip, CircularProgress, Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloseOutlined, DeleteOutlineOutlined,
  LinkOutlined, PlayCircleOutlineOutlined,
  CollectionsOutlined, CodeOutlined,
  ChatBubbleOutlineOutlined, OndemandVideoOutlined, ForumOutlined, GridViewOutlined, StorefrontOutlined, AddOutlined, DeleteOutlineOutlined as DeleteIcon,
  ContentCopyOutlined, FiberManualRecord, StopCircleOutlined, RefreshOutlined,
  TabletOutlined, PhoneIphoneOutlined, VisibilityOutlined,
} from '@mui/icons-material';
import { PRIMARY, SHAPE_TYPES, LOBBY_BREAKPOINTS, resolveHotspotForBp, setHotspotBpOverride, isHotspotVisibleOnBp } from '../constants';
import { InspSectionTitle, InspRow, InspInput, ToggleRow, selectStyle } from '../ui/primitives';
import { MediaField } from '../MediaField';
import { ContentBrowserModal } from '../ContentBrowserModal';
import { api } from '../../../../hooks/useApi';

const CHAT_COLOR = '#0a7aff';

// ─── Hotspot type definitions ────────────────────────────────────────────────

const HOTSPOT_TYPES = [
  {
    key: 'link',
    label: 'Link To',
    desc: 'Navigate to a lobby, plenary, or external URL',
    icon: <LinkOutlined sx={{ fontSize: 15 }} />,
    color: '#1075BA',
  },
  {
    key: 'video',
    label: 'Video Pop-up',
    desc: 'Open a video player in a modal',
    icon: <PlayCircleOutlineOutlined sx={{ fontSize: 15 }} />,
    color: '#e63946',
  },
  {
    key: 'gallery',
    label: 'Image Gallery Pop-up',
    desc: 'Open a lightbox image gallery',
    icon: <CollectionsOutlined sx={{ fontSize: 15 }} />,
    color: '#7209b7',
  },
  {
    key: 'iframe',
    label: 'iFrame Pop-up',
    desc: 'Embed any web content in a modal',
    icon: <CodeOutlined sx={{ fontSize: 15 }} />,
    color: '#2d9e4e',
  },
  {
    key: 'chat',
    label: 'Live Chat Panel',
    desc: 'Open a live chat panel for attendees',
    icon: <ChatBubbleOutlineOutlined sx={{ fontSize: 15 }} />,
    color: CHAT_COLOR,
  },
  {
    key: 'stream',
    label: 'Livestream Player',
    desc: 'Embedded video player on the canvas',
    icon: <OndemandVideoOutlined sx={{ fontSize: 15 }} />,
    color: '#EF4444',
  },
  {
    key: 'livestream-chat',
    label: 'Livestream Chat',
    desc: 'Full-height live chat pinned to the canvas',
    icon: <ForumOutlined sx={{ fontSize: 15 }} />,
    color: '#0a7aff',
  },
  // {
  //   key:   'exhibitor-grid',
  //   label: 'Exhibitor Grid',
  //   desc:  'Grid of exhibitor booths on the canvas',
  //   icon:  <GridViewOutlined sx={{ fontSize: 15 }} />,
  //   color: '#0d7a5f',
  // },
];

// ─── Type selector ───────────────────────────────────────────────────────────

function TypeSelector({ value, onChange }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {HOTSPOT_TYPES.map(t => {
        const active = value === t.key;
        return (
          <Box
            key={t.key}
            onClick={() => onChange(t.key)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25,
              px: 1.5, py: 1,
              borderRadius: '8px',
              border: `1px solid ${active ? alpha(t.color, 0.5) : '#e8edf2'}`,
              background: active ? alpha(t.color, 0.07) : '#fff',
              cursor: 'pointer',
              transition: 'all 0.14s',
              '&:hover': { border: `1px solid ${alpha(t.color, 0.4)}`, background: alpha(t.color, 0.04) },
            }}
          >
            <Box
              sx={{
                width: 28, height: 28, borderRadius: '6px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: active ? t.color : alpha(t.color, 0.1),
                color: active ? '#fff' : t.color,
                transition: 'all 0.14s',
              }}
            >
              {t.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: active ? t.color : '#2c3444', lineHeight: 1.2 }}>
                {t.label}
              </Typography>
              <Typography sx={{ fontSize: '0.66rem', color: '#9ea8b3', lineHeight: 1.3 }}>
                {t.desc}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Link-To sub-panel ───────────────────────────────────────────────────────

function LinkToPanel({ hotspot, lobbies = [], plenaries = [], upd }) {
  const linkTarget = hotspot.hotspotAction?.linkTarget || 'lobby';

  const allOptions = [
    ...lobbies.map(l => ({ label: l.name, id: l._id, group: 'Lobbies' })),
    ...plenaries.map(p => ({ label: p.name, id: p._id, group: 'Plenaries' })),
  ];

  const updAction = patch =>
    upd({ hotspotAction: { ...hotspot.hotspotAction, ...patch } });

  return (
    <>
      <InspRow label="Destination Type">
        <select
          value={linkTarget}
          onChange={e => updAction({ linkTarget: e.target.value, targetId: null, externalUrl: null })}
          style={selectStyle}
        >
          <option value="lobby">Lobby</option>
          <option value="plenary">Plenary</option>
          <option value="external">External URL</option>
        </select>
      </InspRow>

      {(linkTarget === 'lobby' || linkTarget === 'plenary') && (
        <InspRow label="Search & Select">
          <Autocomplete
            size="small"
            options={allOptions.filter(o =>
              linkTarget === 'lobby' ? o.group === 'Lobbies' : o.group === 'Plenaries'
            )}
            groupBy={o => o.group}
            getOptionLabel={o => o.label}
            value={allOptions.find(o => o.id === hotspot.hotspotAction?.targetId) || null}
            onChange={(_, v) => updAction({ targetId: v?.id || null })}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={`Search ${linkTarget === 'lobby' ? 'lobbies' : 'plenaries'}…`}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.75rem',
                    borderRadius: '6px',
                    '& fieldset': { borderColor: '#e4e9f0' },
                    '&:hover fieldset': { borderColor: alpha(PRIMARY, 0.4) },
                    '&.Mui-focused fieldset': { borderColor: PRIMARY },
                  },
                }}
              />
            )}
            sx={{ width: '100%' }}
          />
        </InspRow>
      )}

      {linkTarget === 'external' && (
        <InspRow label="URL">
          <InspInput
            value={hotspot.hotspotAction?.externalUrl || ''}
            onChange={v => updAction({ externalUrl: v })}
            placeholder="https://…"
          />
        </InspRow>
      )}

      <InspRow label="Open in">
        <select
          value={hotspot.hotspotAction?.openIn || 'same'}
          onChange={e => updAction({ openIn: e.target.value })}
          style={selectStyle}
        >
          <option value="same">Same tab</option>
          <option value="blank">New tab</option>
        </select>
      </InspRow>
    </>
  );
}

// ─── Video pop-up sub-panel ──────────────────────────────────────────────────

function VideoPanel({ hotspot, upd }) {
  const updAction = patch =>
    upd({ hotspotAction: { ...hotspot.hotspotAction, ...patch } });
  const provider = hotspot.hotspotAction?.videoProvider || 'url';

  return (
    <>
      <InspRow label="Video Source">
        <select
          value={provider}
          onChange={e => updAction({ videoProvider: e.target.value, videoUrl: '' })}
          style={selectStyle}
        >
          <option value="url">Direct URL (.mp4)</option>
          <option value="youtube">YouTube</option>
          <option value="vimeo">Vimeo</option>
        </select>
      </InspRow>

      {provider === 'url' ? (
        <InspRow label="Video File">
          <MediaField
            value={hotspot.hotspotAction?.videoUrl || ''}
            onChange={v => updAction({ videoUrl: v })}
            mediaType="video"
          />
        </InspRow>
      ) : (
        <InspRow label={provider === 'youtube' ? 'YouTube URL or ID' : 'Vimeo URL or ID'}>
          <InspInput
            value={hotspot.hotspotAction?.videoUrl || ''}
            onChange={v => updAction({ videoUrl: v })}
            placeholder={provider === 'youtube' ? 'https://youtube.com/watch?v=… or video ID' : 'https://vimeo.com/… or video ID'}
          />
        </InspRow>
      )}

      <ToggleRow
        label="Autoplay"
        value={hotspot.hotspotAction?.autoplay || false}
        onChange={v => updAction({ autoplay: v })}
      />
      <ToggleRow
        label="Show controls"
        value={hotspot.hotspotAction?.controls !== false}
        onChange={v => updAction({ controls: v })}
      />
    </>
  );
}


// ─── GalleryAddButton ─────────────────────────────────────────────────────────

function GalleryAddButton({ onAdd }) {
  const [browserOpen, setBrowserOpen] = React.useState(false);
  const [urlInput, setUrlInput] = React.useState('');
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box
          onClick={() => setBrowserOpen(true)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75,
            border: '1.5px solid #e4e9f0',
            borderRadius: '8px',
            px: 1.25, py: 0.75,
            cursor: 'pointer',
            transition: 'all 0.13s',
            background: '#fff',
            '&:hover': { borderColor: '#1075BA', background: 'rgba(16,117,186,0.03)' },
          }}
        >
          <span style={{ fontSize: 14, color: '#1075BA' }}>🖼</span>
          <Typography sx={{ fontSize: '0.69rem', fontWeight: 600, color: '#3c4555', flex: 1 }}>
            Browse Image Library
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <input
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && urlInput.trim()) { onAdd(urlInput); setUrlInput(''); } }}
            placeholder="Or paste URL and press Enter"
            style={{
              flex: 1, height: 30, padding: '0 8px',
              border: '1px solid #e4e9f0', borderRadius: '6px',
              fontSize: '0.73rem', color: '#3c4555',
              outline: 'none', background: '#fff', fontFamily: 'inherit',
            }}
          />
          <Box
            onClick={() => { if (urlInput.trim()) { onAdd(urlInput); setUrlInput(''); } }}
            sx={{
              px: 1.25, height: 30, borderRadius: '6px',
              background: '#1075BA', color: '#fff',
              display: 'flex', alignItems: 'center',
              fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
              '&:hover': { background: 'rgba(16,117,186,0.85)' },
              flexShrink: 0,
            }}
          >
            Add
          </Box>
        </Box>
      </Box>
      <ContentBrowserModal
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onSelect={(url) => onAdd(url)}
        mediaType="image"
      />
    </>
  );
}

// ─── Image gallery sub-panel ─────────────────────────────────────────────────

function GalleryPanel({ hotspot, upd }) {
  const [newUrl, setNewUrl] = useState('');
  const images = hotspot.hotspotAction?.images || [];

  const addImage = () => {
    const trimmed = newUrl.trim();
    if (!trimmed) return;
    upd({ hotspotAction: { ...hotspot.hotspotAction, images: [...images, { url: trimmed, caption: '' }] } });
    setNewUrl('');
  };

  const removeImage = idx =>
    upd({ hotspotAction: { ...hotspot.hotspotAction, images: images.filter((_, i) => i !== idx) } });

  const updateCaption = (idx, caption) =>
    upd({
      hotspotAction: {
        ...hotspot.hotspotAction,
        images: images.map((img, i) => i === idx ? { ...img, caption } : img),
      },
    });

  return (
    <>
      {images.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
          {images.map((img, idx) => (
            <Box
              key={idx}
              sx={{
                borderRadius: '8px', border: '1px solid #e8edf2', overflow: 'hidden',
                background: '#fafbfc',
              }}
            >
              <Box
                sx={{
                  height: 72, background: '#e8edf2',
                  backgroundImage: `url(${img.url})`,
                  backgroundSize: 'cover', backgroundPosition: 'center',
                  position: 'relative',
                }}
              >
                <Box
                  onClick={() => removeImage(idx)}
                  sx={{
                    position: 'absolute', top: 4, right: 4,
                    width: 20, height: 20, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.55)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', fontSize: '0.65rem',
                    '&:hover': { background: '#dc2626' },
                  }}
                >
                  <CloseOutlined sx={{ fontSize: 12 }} />
                </Box>
                <Chip
                  label={`#${idx + 1}`}
                  size="small"
                  sx={{ position: 'absolute', bottom: 4, left: 4, height: 18, fontSize: '0.6rem', background: 'rgba(0,0,0,0.45)', color: '#fff' }}
                />
              </Box>
              <Box sx={{ px: 1, py: 0.75 }}>
                <input
                  value={img.caption || ''}
                  onChange={e => updateCaption(idx, e.target.value)}
                  placeholder="Caption (optional)"
                  style={{
                    width: '100%', border: 'none', outline: 'none',
                    fontSize: '0.72rem', color: '#5a6474', background: 'transparent',
                    fontFamily: 'inherit',
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <InspRow label="Add Image">
        <GalleryAddButton onAdd={(url) => {
          const trimmed = url.trim();
          if (!trimmed) return;
          upd({ hotspotAction: { ...hotspot.hotspotAction, images: [...images, { url: trimmed, caption: '' }] } });
        }} />
      </InspRow>
    </>
  );
}

// ─── iFrame sub-panel ────────────────────────────────────────────────────────

function IframePanel({ hotspot, upd }) {
  const updAction = patch =>
    upd({ hotspotAction: { ...hotspot.hotspotAction, ...patch } });

  return (
    <>
      <InspRow label="Embed Code or URL">
        <Box sx={{ width: '100%' }}>
          <textarea
            value={hotspot.hotspotAction?.embedCode || ''}
            onChange={e => updAction({ embedCode: e.target.value })}
            placeholder={'Paste an <iframe> embed code\nor a direct URL'}
            rows={5}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #e4e9f0',
              borderRadius: '8px',
              fontSize: '0.72rem',
              fontFamily: 'monospace',
              color: '#3c4555',
              resize: 'vertical',
              outline: 'none',
              background: '#fafbfc',
              boxSizing: 'border-box',
              lineHeight: 1.6,
            }}
          />
          <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8', mt: 0.5 }}>
            Supports raw &lt;iframe&gt; tags or plain URLs
          </Typography>
        </Box>
      </InspRow>

      <InspRow label="Modal Width">
        <select
          value={hotspot.hotspotAction?.modalWidth || 'md'}
          onChange={e => updAction({ modalWidth: e.target.value })}
          style={selectStyle}
        >
          <option value="sm">Small (480px)</option>
          <option value="md">Medium (720px)</option>
          <option value="lg">Large (960px)</option>
          <option value="xl">Extra Large (1200px)</option>
          <option value="full">Full Screen</option>
        </select>
      </InspRow>
    </>
  );
}

// ─── Chat panel sub-panel ─────────────────────────────────────────────────────

function ChatPanel({ hotspot, upd }) {
  const updAction = patch =>
    upd({ hotspotAction: { ...hotspot.hotspotAction, ...patch } });

  const channelId = hotspot.hotspotAction?.channelId || null;
  const mode = hotspot.hotspotAction?.mode || 'group';

  return (
    <>
      {/* Channel status */}
      <Box sx={{
        mb: 2, p: 1.25, borderRadius: '8px',
        background: channelId ? alpha(CHAT_COLOR, 0.06) : alpha('#f59e0b', 0.07),
        border: `1px solid ${channelId ? alpha(CHAT_COLOR, 0.18) : alpha('#f59e0b', 0.25)}`,
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        <Box sx={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
          background: channelId ? '#22c55e' : '#f59e0b',
        }} />
        <Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: channelId ? CHAT_COLOR : '#b45309' }}>
            {channelId ? 'Channel active' : 'Channel will be created on save'}
          </Typography>
          {channelId && (
            <Typography sx={{ fontSize: '0.62rem', color: '#9ea8b3', mt: 0.2, fontFamily: 'monospace' }}>
              {channelId}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Mode selector ── */}
      <InspSectionTitle label="Chat Mode" color={CHAT_COLOR} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', mb: 2 }}>
        {[
          {
            key: 'group',
            label: 'Group Chat',
            desc: 'Everyone sees all messages — like a public channel',
            icon: '👥',
          },
          {
            key: 'direct',
            label: 'Direct / Support',
            desc: 'Each attendee gets a private 1-on-1 chat with your team',
            icon: '💬',
          },
        ].map(opt => {
          const active = mode === opt.key;
          return (
            <Box
              key={opt.key}
              onClick={() => updAction({ mode: opt.key })}
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.25,
                px: 1.5, py: 1, borderRadius: '8px', cursor: 'pointer',
                border: `1px solid ${active ? alpha(CHAT_COLOR, 0.5) : '#e8edf2'}`,
                background: active ? alpha(CHAT_COLOR, 0.07) : '#fff',
                transition: 'all 0.14s',
                '&:hover': { border: `1px solid ${alpha(CHAT_COLOR, 0.35)}`, background: alpha(CHAT_COLOR, 0.04) },
              }}
            >
              <Box sx={{
                width: 30, height: 30, borderRadius: '7px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: active ? CHAT_COLOR : alpha(CHAT_COLOR, 0.1),
                fontSize: '0.9rem',
              }}>
                {opt.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: active ? CHAT_COLOR : '#2c3444', lineHeight: 1.2 }}>
                  {opt.label}
                </Typography>
                <Typography sx={{ fontSize: '0.66rem', color: '#9ea8b3', lineHeight: 1.3, mt: 0.2 }}>
                  {opt.desc}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      <InspSectionTitle label="Settings" color="#9ea8b3" />

      <InspRow label="Chat Panel Title">
        <InspInput
          value={hotspot.hotspotAction?.chatTitle || ''}
          onChange={v => updAction({ chatTitle: v })}
          placeholder="Live Chat"
        />
      </InspRow>

      <InspRow label="Panel Width">
        <select
          value={hotspot.hotspotAction?.panelWidth || 'md'}
          onChange={e => updAction({ panelWidth: e.target.value })}
          style={selectStyle}
        >
          <option value="sm">Narrow (340px)</option>
          <option value="md">Medium (420px)</option>
          <option value="lg">Wide (520px)</option>
        </select>
      </InspRow>

      <ToggleRow
        label="Show participant count"
        value={hotspot.hotspotAction?.showParticipantCount !== false}
        onChange={v => updAction({ showParticipantCount: v })}
      />

      <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8', mt: 1, lineHeight: 1.6 }}>
        {mode === 'direct'
          ? 'In Direct mode, each attendee sees only their own conversation with your team. Admins see all threads.'
          : 'In Group mode, all attendees share one conversation — great for Q&A and announcements.'}
      </Typography>
    </>
  );
}

// ─── Main inspector ──────────────────────────────────────────────────────────

/**
 * Left-panel inspector shown when a hotspot shape is selected.
 *
 * Props:
 *   hotspot   — the selected hotspot object
 *   lobbies   — array of { _id, name } for link-to dropdown
 *   plenaries — array of { _id, name } for link-to dropdown
 *   onUpdate  — (updatedHotspot) => void
 *   onDelete  — () => void
 *   onClose   — () => void
 */


// ─── Livestream Chat panel ────────────────────────────────────────────────────
function LivestreamChatPanel({ hotspot, upd }) {
  const action = hotspot.hotspotAction || {};
  const meta = hotspot.meta || {};
  const updAction = patch => upd({ hotspotAction: { ...action, ...patch } });
  const updMeta = patch => upd({ meta: { ...meta, ...patch } });
  const side = meta.side || 'right';
  const chatW = 22;

  const setSide = (newSide) => {
    const chatX = newSide === 'left' ? 0 : 100 - chatW;
    upd({
      meta: { ...meta, side: newSide, width: chatW, height: 100 },
      points: [{ x: chatX, y: 0 }],
    });
  };

  return (
    <>
      <InspSectionTitle label="Position" color="#0a7aff" />
      <InspRow label="Side">
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {['left', 'right'].map(s => (
            <Box
              key={s}
              onClick={() => setSide(s)}
              sx={{
                flex: 1, textAlign: 'center', py: 0.6, borderRadius: 1,
                fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                background: side === s ? '#0a7aff' : '#f0f4f8',
                color: side === s ? '#fff' : '#7a8494',
                border: `1px solid ${side === s ? '#0a7aff' : '#e4e9f0'}`,
                textTransform: 'capitalize',
                transition: 'all 0.15s',
              }}
            >
              {s}
            </Box>
          ))}
        </Box>
      </InspRow>

      <InspSectionTitle label="Chat Channel" color="#0a7aff" />
      <Typography sx={{ fontSize: '0.67rem', color: '#9ea8b3', mb: 1.25, lineHeight: 1.5 }}>
        A channel is auto-created on save. You can also paste an existing channel ID.
      </Typography>
      <InspRow label="Channel ID">
        <InspInput
          value={action.channelId || ''}
          onChange={v => updAction({ channelId: v || null })}
          placeholder="Auto-created on save"
        />
      </InspRow>
      <InspRow label="Title">
        <InspInput
          value={action.chatTitle || ''}
          onChange={v => updAction({ chatTitle: v })}
          placeholder="Livestream Chat"
        />
      </InspRow>
    </>
  );
}

// ─── Livestream panel ────────────────────────────────────────────────────────

// Copyable credential field
function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <InspRow label={label}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
        <Box
          component="input"
          readOnly
          value={value}
          sx={{
            flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: '0.65rem',
            background: '#f0f4f8', border: '1px solid #dce3ea', borderRadius: '6px',
            px: 1, py: 0.6, color: '#334155', outline: 'none',
          }}
        />
        <IconButton size="small" onClick={copy} sx={{ flexShrink: 0, color: copied ? '#16a34a' : '#64748b' }}>
          <ContentCopyOutlined sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </InspRow>
  );
}

const MUX_STATUS_CFG = {
  idle: { label: 'Idle', color: '#64748b', bg: '#f1f5f9' },
  active: { label: 'Live', color: '#16a34a', bg: '#dcfce7' },
  disabled: { label: 'Ended', color: '#dc2626', bg: '#fee2e2' },
  unknown: { label: 'Unknown', color: '#9ea8b3', bg: '#f1f5f9' },
};

function MuxStatusChip({ status, loading }) {
  const cfg = MUX_STATUS_CFG[status] || MUX_STATUS_CFG.unknown;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.3, borderRadius: '20px',
      background: cfg.bg, color: cfg.color,
      fontSize: '0.68rem', fontWeight: 700,
    }}>
      {loading
        ? <CircularProgress size={8} sx={{ color: cfg.color }} />
        : <FiberManualRecord sx={{ fontSize: 8 }} />
      }
      {cfg.label}
    </Box>
  );
}

function StreamPanel({ hotspot, upd, onSave }) {
  const action = hotspot.hotspotAction || {};
  const updAction = useCallback(patch => upd({ hotspotAction: { ...action, ...patch } }), [action, upd]);

  const [muxLoading, setMuxLoading] = useState(false);
  const [muxStatus, setMuxStatus] = useState(action.muxStatus || null);
  const [statusLoading, setStatusLoading] = useState(false);
  const pollRef = useRef(null);

  // ── Modal state ───────────────────────────────────────────────────────────
  const [errorModal, setErrorModal] = useState(null);   // { title, message }
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const showError = useCallback((title, message) => setErrorModal({ title, message }), []);

  const pollStatus = useCallback(async (streamId) => {
    if (!streamId) return;
    setStatusLoading(true);
    try {
      const { data: res } = await api.get(`/mux/livestreams/${streamId}`);
      if (res.success) {
        setMuxStatus(res.data.muxStatus);
        updAction({ muxStatus: res.data.muxStatus, muxPlaybackId: res.data.muxPlaybackId });
      }
    } catch (err) {
      // 404 right after creation is expected — Mux needs a moment to provision
      const status = err?.response?.status;
      if (status !== 404) console.warn('[Mux] poll error:', err?.response?.data?.message || err.message);
    } finally { setStatusLoading(false); }
  }, [updAction]);

  useEffect(() => {
    if (!action.muxStreamId) return;
    // Delay the first poll by 3 s — Mux needs a moment to provision a new stream
    const initialDelay = setTimeout(() => {
      pollStatus(action.muxStreamId);
      pollRef.current = setInterval(() => pollStatus(action.muxStreamId), 8000);
    }, 3000);
    return () => { clearTimeout(initialDelay); clearInterval(pollRef.current); };
  }, [action.muxStreamId]); // eslint-disable-line

  const handleCreateStream = async () => {
    setMuxLoading(true);
    try {
      const { data: res } = await api.post('/mux/livestreams', { label: hotspot.label || 'Livestream Player' });
      if (res.success) {
        const patch = {
          streamType: 'mux',
          muxStreamId: res.data.muxStreamId,
          muxStreamKey: res.data.muxStreamKey,
          muxRtmpUrl: res.data.muxRtmpUrl,
          muxPlaybackId: res.data.muxPlaybackId,
          muxStatus: res.data.muxStatus,
        };
        const updatedHotspot = { ...hotspot, hotspotAction: { ...action, ...patch } };
        upd({ hotspotAction: { ...action, ...patch } });
        setMuxStatus(res.data.muxStatus);
        // Pass the fully-merged hotspot so save doesn't read stale state
        await onSave?.(updatedHotspot);
      }
    } catch (err) {
      showError('Failed to create stream', err?.response?.data?.message || err.message);
    } finally {
      setMuxLoading(false);
    }
  };

  const commitEndStream = async () => {
    setEndConfirmOpen(false);
    if (!action.muxStreamId) return;
    setMuxLoading(true);
    try {
      await api.delete(`/mux/livestreams/${action.muxStreamId}`);
      clearInterval(pollRef.current);
      const updatedHotspot = { ...hotspot, hotspotAction: { ...action, muxStatus: 'disabled' } };
      upd({ hotspotAction: { ...action, muxStatus: 'disabled' } });
      setMuxStatus('disabled');
      await onSave?.(updatedHotspot);
    } catch (err) {
      showError('Failed to end stream', err?.response?.data?.message || err.message);
    } finally {
      setMuxLoading(false);
    }
  };

  const handleReEnable = async () => {
    if (!action.muxStreamId) return;
    setMuxLoading(true);
    try {
      await api.post(`/mux/livestreams/${action.muxStreamId}/enable`);
      const updatedHotspot = { ...hotspot, hotspotAction: { ...action, muxStatus: 'idle' } };
      upd({ hotspotAction: { ...action, muxStatus: 'idle' } });
      setMuxStatus('idle');
      await onSave?.(updatedHotspot);
    } catch (err) {
      showError('Failed to re-enable stream', err?.response?.data?.message || err.message);
    } finally {
      setMuxLoading(false);
    }
  };

  // Deletes the Mux stream from the API and clears all mux fields from the element
  const commitDeleteStream = async () => {
    setDeleteConfirmOpen(false);
    if (!action.muxStreamId) return;
    setMuxLoading(true);
    try {
      await api.delete(`/mux/livestreams/${action.muxStreamId}/delete`).catch(() => { });
      clearInterval(pollRef.current);
      const cleared = {
        muxStreamId: null, muxStreamKey: null,
        muxRtmpUrl: null, muxPlaybackId: null, muxStatus: null, streamType: 'iframe',
      };
      const updatedHotspot = { ...hotspot, hotspotAction: { ...action, ...cleared } };
      upd({ hotspotAction: { ...action, ...cleared } });
      setMuxStatus(null);
      await onSave?.(updatedHotspot);
    } catch (err) {
      showError('Failed to delete stream', err?.response?.data?.message || err.message);
    } finally {
      setMuxLoading(false);
    }
  };

  const hasMuxStream = !!action.muxStreamId;

  return (
    <>
      <InspSectionTitle label="Live Stream" color="#EF4444" />

      {/* No stream yet */}
      {!hasMuxStream && (
        <InspRow label="">
          <Box
            component="button"
            onClick={handleCreateStream}
            disabled={muxLoading}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 2, py: 1, borderRadius: '8px', border: 'none',
              background: muxLoading ? '#f1f5f9' : '#EF4444',
              color: muxLoading ? '#94a3b8' : '#fff',
              fontWeight: 600, fontSize: '0.75rem',
              cursor: muxLoading ? 'not-allowed' : 'pointer',
              width: '100%', justifyContent: 'center',
              transition: 'background 0.15s',
              '&:hover': { background: muxLoading ? '#f1f5f9' : '#dc2626' },
            }}
          >
            {muxLoading
              ? <><CircularProgress size={12} sx={{ color: '#94a3b8', mr: 0.75 }} /> Creating stream…</>
              : '+ Create Livestream'
            }
          </Box>
        </InspRow>
      )}

      {/* Stream exists */}
      {hasMuxStream && (
        <>
          {/* Status */}
          <InspRow label="Status">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MuxStatusChip status={muxStatus} loading={statusLoading} />
              <Tooltip title="Refresh status">
                <IconButton
                  size="small"
                  onClick={() => pollStatus(action.muxStreamId)}
                  disabled={statusLoading}
                  sx={{ color: '#94a3b8' }}
                >
                  <RefreshOutlined sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </InspRow>

          {/* Encoder credentials */}
          <InspSectionTitle label="Encoder Settings" color="#9ea8b3" />
          <CopyField label="RTMP URL" value={action.muxRtmpUrl || 'rtmps://global-live.mux.com:443/app'} />
          <CopyField label="Stream Key" value={action.muxStreamKey || ''} />

          {/* Controls */}
          <InspSectionTitle label="Controls" color="#9ea8b3" />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 1.5 }}>

            {/* End / Re-enable */}
            {muxStatus === 'disabled' ? (
              <Box
                component="button"
                onClick={handleReEnable}
                disabled={muxLoading}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 0.75, px: 1.5, py: 0.8, borderRadius: '8px', border: 'none',
                  background: '#16a34a', color: '#fff',
                  fontWeight: 600, fontSize: '0.72rem',
                  cursor: muxLoading ? 'not-allowed' : 'pointer',
                  '&:hover': { background: '#15803d' },
                }}
              >
                {muxLoading && <CircularProgress size={10} sx={{ color: '#fff', mr: 0.5 }} />}
                Re-enable Stream
              </Box>
            ) : (
              <Box
                component="button"
                onClick={() => setEndConfirmOpen(true)}
                disabled={muxLoading}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 0.75, px: 1.5, py: 0.8, borderRadius: '8px', border: 'none',
                  background: muxLoading ? '#f1f5f9' : '#fee2e2',
                  color: muxLoading ? '#94a3b8' : '#dc2626',
                  fontWeight: 600, fontSize: '0.72rem',
                  cursor: muxLoading ? 'not-allowed' : 'pointer',
                  '&:hover': { background: '#fecaca' },
                }}
              >
                {muxLoading
                  ? <CircularProgress size={10} sx={{ color: '#dc2626', mr: 0.5 }} />
                  : <StopCircleOutlined sx={{ fontSize: 14 }} />
                }
                End Stream
              </Box>
            )}

            {/* Delete stream permanently */}
            {muxStatus === 'disabled' &&
              <Box
                component="button"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={muxLoading}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 0.75, px: 1.5, py: 0.8, borderRadius: '8px',
                  border: '1px solid #fca5a5',
                  background: 'transparent', color: '#dc2626',
                  fontWeight: 600, fontSize: '0.72rem',
                  cursor: muxLoading ? 'not-allowed' : 'pointer',
                  '&:hover': { background: '#fff1f1' },
                }}
              >
                <DeleteOutlineOutlined sx={{ fontSize: 14 }} />
                Delete Stream
              </Box>
            }
          </Box>
        </>
      )}

      {/* ── End stream confirm modal ── */}
      {endConfirmOpen && (
        <Box onClick={e => e.stopPropagation()} sx={{ position: 'fixed', inset: 0, zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}>
          <Box sx={{ background: '#fff', borderRadius: '14px', boxShadow: '0 16px 48px rgba(0,0,0,0.18)', p: 3, width: 380, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <StopCircleOutlined sx={{ fontSize: 18, color: '#dc2626' }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', mb: 0.5 }}>End this livestream?</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#5a6474', lineHeight: 1.55 }}>
                  The stream key will be disabled and viewers will no longer be able to connect. You can re-enable it later.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Box onClick={() => setEndConfirmOpen(false)} sx={{ px: 2, py: 0.85, borderRadius: '8px', border: '1.5px solid #e4e9f0', fontSize: '0.8rem', fontWeight: 600, color: '#5a6474', cursor: 'pointer', '&:hover': { background: '#f8f9fb' } }}>Cancel</Box>
              <Box onClick={commitEndStream} sx={{ px: 2, py: 0.85, borderRadius: '8px', background: '#dc2626', fontSize: '0.8rem', fontWeight: 700, color: '#fff', cursor: 'pointer', '&:hover': { background: '#b91c1c' } }}>End Stream</Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Delete stream confirm modal ── */}
      {deleteConfirmOpen && (
        <Box onClick={e => e.stopPropagation()} sx={{ position: 'fixed', inset: 0, zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}>
          <Box sx={{ background: '#fff', borderRadius: '14px', boxShadow: '0 16px 48px rgba(0,0,0,0.18)', p: 3, width: 380, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Box sx={{ fontSize: 18 }}>⚠️</Box>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', mb: 0.5 }}>Delete stream permanently?</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#5a6474', lineHeight: 1.55 }}>
                  This will permanently delete the livestream and all associated credentials. This action cannot be undone.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Box onClick={() => setDeleteConfirmOpen(false)} sx={{ px: 2, py: 0.85, borderRadius: '8px', border: '1.5px solid #e4e9f0', fontSize: '0.8rem', fontWeight: 600, color: '#5a6474', cursor: 'pointer', '&:hover': { background: '#f8f9fb' } }}>Cancel</Box>
              <Box onClick={commitDeleteStream} sx={{ px: 2, py: 0.85, borderRadius: '8px', background: '#dc2626', fontSize: '0.8rem', fontWeight: 700, color: '#fff', cursor: 'pointer', '&:hover': { background: '#b91c1c' } }}>Delete Stream</Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Error modal ── */}
      {errorModal && (
        <Box onClick={e => e.stopPropagation()} sx={{ position: 'fixed', inset: 0, zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}>
          <Box sx={{ background: '#fff', borderRadius: '14px', boxShadow: '0 16px 48px rgba(0,0,0,0.18)', p: 3, width: 360, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Box sx={{ fontSize: 18 }}>❌</Box>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', mb: 0.5 }}>{errorModal.title}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#5a6474', lineHeight: 1.55 }}>{errorModal.message}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box onClick={() => setErrorModal(null)} sx={{ px: 2, py: 0.85, borderRadius: '8px', background: '#1a1a2e', fontSize: '0.8rem', fontWeight: 600, color: '#fff', cursor: 'pointer', '&:hover': { background: '#2d2d4e' } }}>OK</Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}


// ─── Exhibitor Grid panel ─────────────────────────────────────────────────────
function SliderRow({ label, value, min, max, step = 1, onChange }) {
  return (
    <InspRow label={`${label}: ${value}`}>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: '#0d7a5f' }}
      />
    </InspRow>
  );
}

function ExhibitorGridPanel({ hotspot, upd, exhibitors = [], selectedLobbyExhibitorId, onSelectLobbyExhibitor, onAddExhibitor, onDeleteExhibitor, onUpdateExhibitor, breakpoint, onUpdateResponsive }) {
  const GRID_COLOR = '#0d7a5f';
  const meta = hotspot.meta || {};

  const bp        = breakpoint || 'desktop';
  const isDesktop = bp === 'desktop';
  const bpDef     = LOBBY_BREAKPOINTS.find(b => b.key === bp) || LOBBY_BREAKPOINTS[0];

  // Per-bp meta override — the fields we allow overriding per breakpoint
  const bpMeta       = !isDesktop ? (hotspot.responsive?.[bp]?.meta || {}) : {};
  const hasGridBpOverride = !isDesktop && Object.keys(bpMeta).some(k =>
    ['gridCardTemplate','gridDisplayMode','gridTileSize','gridGap'].includes(k)
  );

  const isNarrowBp = bp === 'tablet-portrait' || bp === 'mobile';

  // Defaults vary by breakpoint: narrow bps use larger tiles and more spacing
  const tileSizeDefault = isNarrowBp ? 450 : 250;
  const gapDefault      = isNarrowBp ? 40  : 8;

  // Slider ranges also vary by breakpoint
  const tileSizeMin = isNarrowBp ? 370  : 180;
  const tileSizeMax = isNarrowBp ? 600  : 400;
  const gapMin      = isNarrowBp ? 20   : 0;
  const gapMax      = isNarrowBp ? 100  : 40;

  // Resolved values: bp override first, then desktop meta fallback, then bp-appropriate default
  const tileSize    = (isDesktop ? meta.gridTileSize    : (bpMeta.gridTileSize    ?? meta.gridTileSize))    ?? tileSizeDefault;
  const gap         = (isDesktop ? meta.gridGap         : (bpMeta.gridGap         ?? meta.gridGap))         ?? gapDefault;
  const cardTemplate = (isDesktop ? meta.gridCardTemplate : (bpMeta.gridCardTemplate ?? meta.gridCardTemplate)) ?? 'masked';
  const displayMode  = (isDesktop ? meta.gridDisplayMode  : (bpMeta.gridDisplayMode  ?? meta.gridDisplayMode))  ?? 'paged';

  // Writer — desktop writes to meta, non-desktop writes to responsive[bp].meta
  const updGridMeta = (patch) => {
    if (isDesktop) {
      upd({ meta: { ...meta, ...patch } });
    } else {
      onUpdateResponsive?.(hotspot.id, bp, { meta: { ...bpMeta, ...patch } });
    }
  };

  const resetBpOverride = () => {
    if (isDesktop) return;
    // Clear only the grid keys from the bp meta, preserve geometry (points, non-grid meta)
    const cleanedMeta = { ...bpMeta };
    ['gridCardTemplate','gridDisplayMode','gridTileSize','gridGap'].forEach(k => delete cleanedMeta[k]);
    const existing = hotspot.responsive?.[bp] || {};
    onUpdateResponsive?.(hotspot.id, bp, { ...existing, meta: cleanedMeta });
  };

  const updMeta = (patch) => updGridMeta(patch);

  const rows = meta.gridRows ?? 2;

  // Selected exhibitor for inline editing
  const selectedEx = selectedLobbyExhibitorId
    ? exhibitors.find(ex => ex._id === selectedLobbyExhibitorId) || null
    : null;

  const updEx = patch => {
    if (!selectedEx) return;
    onUpdateExhibitor?.({ ...selectedEx, ...patch });
  };

  // ── Grid settings + list view ──────────────────────────────────────────────
  return (
    <>
      {/* Breakpoint override indicator */}
      {!isDesktop && (
        <Box sx={{
          mb: 1.5, px: 1.25, py: 0.75, borderRadius: 1.5,
          background: `${bpDef.color}12`,
          border: `1px dashed ${bpDef.color}72`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ color: bpDef.color, display: 'flex', alignItems: 'center' }}>{bpDef.icon}</Box>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: bpDef.color }}>
              {bpDef.label} overrides
            </Typography>
          </Box>
          {hasGridBpOverride && (
            <Box
              onClick={resetBpOverride}
              sx={{ fontSize: '0.62rem', fontWeight: 600, color: bpDef.color, cursor: 'pointer', opacity: 0.75, '&:hover': { opacity: 1 } }}
            >
              Reset
            </Box>
          )}
        </Box>
      )}

      <InspSectionTitle label="Card Style" color={GRID_COLOR} />
      {/* Show a bp-override dot next to the label when this setting is overridden */}
      {!isDesktop && (bpMeta.gridCardTemplate !== undefined) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, mt: -0.75 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: bpDef.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.6rem', color: bpDef.color, fontWeight: 600 }}>Override active</Typography>
        </Box>
      )}

      {/* Template toggle */}
      <Box sx={{ display: 'flex', gap: '6px', mb: 1.5 }}>
        {[
          {
            key: 'flat',
            label: 'Flat',
            desc: 'Full image, overlay footer',
            preview: (
              <Box sx={{ width: '100%', aspectRatio: '1', borderRadius: '5px', overflow: 'hidden', border: '1px solid #e4e9f0', position: 'relative', background: '#1a2540' }}>
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(16,117,186,0.3) 0%, #1a2540 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ fontSize: 18, color: 'rgba(255,255,255,0.3)' }}>🏪</Box>
                </Box>
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.55)', px: '8%', py: '8%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ width: '70%', height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.7)' }} />
                </Box>
              </Box>
            ),
          },
          {
            key: 'masked',
            label: 'Masked',
            desc: 'Booth shape with white footer',
            preview: (
              <Box sx={{ width: '100%', aspectRatio: '1', position: 'relative', overflow: 'hidden', border: '1px solid #e4e9f0', borderRadius: '5px', background: '#f8f9fb' }}>
                {/* Simplified booth shape preview */}
                <Box sx={{ position: 'absolute', top: '5%', left: '10%', right: '10%', height: '65%', background: 'linear-gradient(135deg, rgba(16,117,186,0.3) 0%, #1a2540 100%)', borderRadius: '3px 3px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ fontSize: 14, color: 'rgba(255,255,255,0.3)' }}>🏪</Box>
                </Box>
                <Box sx={{ position: 'absolute', bottom: '5%', left: '10%', right: '10%', height: '22%', background: '#fff', borderRadius: '0 0 3px 3px', border: '1px solid #e4e9f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ width: '60%', height: 3, borderRadius: 2, background: '#1075BA', opacity: 0.5 }} />
                </Box>
              </Box>
            ),
          },
        ].map(t => {
          const isSelected = cardTemplate === t.key;
          return (
            <Box
              key={t.key}
              onClick={() => updMeta({ gridCardTemplate: t.key })}
              sx={{
                flex: 1, borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
                border: `2px solid ${isSelected ? GRID_COLOR : '#e4e9f0'}`,
                background: isSelected ? `rgba(13,122,95,0.04)` : '#fff',
                transition: 'all 0.15s',
                '&:hover': { borderColor: isSelected ? GRID_COLOR : '#c8d4df' },
              }}
            >
              <Box sx={{ p: '8px 8px 0' }}>{t.preview}</Box>
              <Box sx={{ px: 1, py: 0.75 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: isSelected ? GRID_COLOR : '#3c4555', textAlign: 'center' }}>{t.label}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      <InspSectionTitle label="Display Mode" color={GRID_COLOR} />
      {!isDesktop && (bpMeta.gridDisplayMode !== undefined) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, mt: -0.75 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: bpDef.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.6rem', color: bpDef.color, fontWeight: 600 }}>Override active</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: '6px', mb: 1.5 }}>
        {[
          { key: 'paged', label: 'Carousel', icon: '⊞', desc: 'Fixed grid with slide animation' },
          { key: 'scroll', label: 'Scroll', icon: '↕', desc: 'Scrollable list of all items' },
        ].map(m => {
          const isSelected = displayMode === m.key;
          return (
            <Box
              key={m.key}
              onClick={() => updMeta({ gridDisplayMode: m.key })}
              sx={{
                flex: 1, py: 1, px: 0.5, borderRadius: '8px', cursor: 'pointer',
                border: `2px solid ${isSelected ? GRID_COLOR : '#e4e9f0'}`,
                background: isSelected ? 'rgba(13,122,95,0.05)' : '#fff',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                transition: 'all 0.15s',
                '&:hover': { borderColor: isSelected ? GRID_COLOR : '#c8d4df' },
              }}
            >
              <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>{m.icon}</Typography>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: isSelected ? GRID_COLOR : '#3c4555' }}>{m.label}</Typography>
              <Typography sx={{ fontSize: '0.58rem', color: '#9ea8b3', textAlign: 'center', lineHeight: 1.3 }}>{m.desc}</Typography>
            </Box>
          );
        })}
      </Box>

      <InspSectionTitle label="Grid Layout" color={GRID_COLOR} />
      {!isDesktop && (bpMeta.gridTileSize !== undefined || bpMeta.gridGap !== undefined) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, mt: -0.75 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: bpDef.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.6rem', color: bpDef.color, fontWeight: 600 }}>Override active</Typography>
        </Box>
      )}

      <SliderRow label={`Tile Size (${tileSize}px)`} value={tileSize} min={tileSizeMin} max={tileSizeMax} step={4} onChange={v => updMeta({ gridTileSize: v })} />
      <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', mb: 1.5, lineHeight: 1.5 }}>
        Columns and rows are calculated automatically based on the available space.
      </Typography>

      <SliderRow label={`Spacing (${gap}px)`} value={gap} min={gapMin} max={gapMax} onChange={v => updMeta({ gridGap: v })} />

      <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', mt: 1, lineHeight: 1.5 }}>
        Click tiles in the canvas to select and edit individual exhibitors.
      </Typography>
    </>
  );
}

export function HotspotInspector({
  hotspot,
  lobbies = [],
  plenaries = [],
  exhibitors = [],
  selectedLobbyExhibitorId = null,
  onSelectLobbyExhibitor,
  onUpdate,
  onSave,
  onDelete,
  onClose,
  onAddExhibitor,
  onDeleteExhibitor,
  onUpdateExhibitor,
  breakpoint,
  onUpdateResponsive,
}) {
  const shape = SHAPE_TYPES[hotspot.shape] || SHAPE_TYPES.polygon;

  // Derive current hotspot type from stored value, default to 'link'
  const hotspotType = hotspot.hotspotType || 'link';

  const upd = patch => onUpdate({ ...hotspot, ...patch });
  const setType = key => upd({ hotspotType: key, hotspotAction: {} });

  const activeTypeDef = HOTSPOT_TYPES.find(t => t.key === hotspotType) || HOTSPOT_TYPES[0];

  const bp = breakpoint || 'desktop';
  const isDesktop = bp === 'desktop';
  const bpDef = LOBBY_BREAKPOINTS.find(b => b.key === bp) || LOBBY_BREAKPOINTS[0];
  const bpColor = bpDef.color;

  // Resolved hotspot geometry for the active breakpoint
  const resolved = resolveHotspotForBp(hotspot, bp);
  const bpOverride = !isDesktop ? (hotspot.responsive?.[bp] || {}) : null;
  const hasOverride = !isDesktop && bpOverride && (bpOverride.points !== undefined || bpOverride.meta !== undefined);

  // For rect/circle hotspots, expose editable position/size fields for current bp
  const isRectOrCircle = hotspot.shape === 'rect' || hotspot.shape === 'circle';

  const updBpGeometry = (patch) => {
    if (isDesktop || !isRectOrCircle) return;
    onUpdateResponsive?.(hotspot.id, bp, patch);
  };

  const clearBpOverride = () => {
    if (isDesktop) return;
    const newResponsive = { ...(hotspot.responsive || {}) };
    delete newResponsive[bp];
    onUpdate({ ...hotspot, responsive: newResponsive });
  };

  // Current effective geometry values for the inspector fields
  const effPoints = resolved.points || hotspot.points || [];
  const effMeta   = resolved.meta   || hotspot.meta   || {};
  const x = effPoints[0]?.x ?? 0;
  const y = effPoints[0]?.y ?? 0;
  const w = effMeta.width ?? effMeta.radius ?? 10;
  const h = effMeta.height ?? effMeta.radius ?? 10;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* ── Header ── */}
      <Box sx={{
        px: 2, py: 1.75,
        borderBottom: '1px solid #e8edf2',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 26, height: 26, borderRadius: 1.5,
            background: alpha(shape.color, 0.1),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: shape.color,
          }}>
            {shape.icon}
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a2e' }}>
            {hotspotType === 'stream' ? 'Livestream Player' : hotspotType === 'livestream-chat' ? 'Livestream Chat' : hotspotType === 'exhibitor-grid' ? 'Exhibitor Grid' : hotspotType === 'text' ? 'Text Element' : hotspotType === 'image' ? 'Image Element' : `${shape.label} Hotspot`}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#9ea8b3' }}>
          <CloseOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>

        {/* ── Label ── */}
        {hotspotType !== 'exhibitor-grid' && hotspotType !== 'text' && hotspotType !== 'image' && (
          <>
            <InspRow label="Label / Tooltip">
              <InspInput
                value={hotspot.label || ''}
                onChange={v => upd({ label: v })}
                placeholder="Hover tooltip text"
              />
            </InspRow>
            <Divider sx={{ my: 1.5 }} />
          </>
        )}

        {/* ── Type-specific settings ── */}
        {hotspotType !== 'stream' && hotspotType !== 'exhibitor-grid' && hotspotType !== 'text' && hotspotType !== 'image' && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1.25,
          }}>
            <Box sx={{
              width: 22, height: 22, borderRadius: '5px',
              background: activeTypeDef.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', flexShrink: 0,
            }}>
              {activeTypeDef.icon}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#2c3444' }}>
              {activeTypeDef.label} Settings
            </Typography>
          </Box>
        )}

        {hotspotType === 'link' && (
          <LinkToPanel
            hotspot={hotspot}
            lobbies={lobbies}
            plenaries={plenaries}
            upd={upd}
          />
        )}
        {hotspotType === 'video' && (
          <VideoPanel hotspot={hotspot} upd={upd} />
        )}
        {hotspotType === 'gallery' && (
          <GalleryPanel hotspot={hotspot} upd={upd} />
        )}
        {hotspotType === 'iframe' && (
          <IframePanel hotspot={hotspot} upd={upd} />
        )}
        {hotspotType === 'chat' && (
          <ChatPanel hotspot={hotspot} upd={upd} />
        )}
        {hotspotType === 'stream' && (
          <StreamPanel hotspot={hotspot} upd={upd} onSave={onSave} />
        )}
        {hotspotType === 'livestream-chat' && (
          <LivestreamChatPanel hotspot={hotspot} upd={upd} lobbies={lobbies} plenaries={plenaries} />
        )}
        {hotspotType === 'exhibitor-grid' && (
          <ExhibitorGridPanel hotspot={hotspot} upd={upd} exhibitors={exhibitors} selectedLobbyExhibitorId={selectedLobbyExhibitorId} onSelectLobbyExhibitor={onSelectLobbyExhibitor} onAddExhibitor={onAddExhibitor} onDeleteExhibitor={onDeleteExhibitor} onUpdateExhibitor={onUpdateExhibitor} breakpoint={breakpoint} onUpdateResponsive={onUpdateResponsive} />
        )}

        <Divider sx={{ my: 1.5 }} />

        {hotspotType !== 'exhibitor-grid' && hotspotType !== 'text' && hotspotType !== 'image' && (
          <>
            <ToggleRow label="Active" value={hotspot.isActive !== false} onChange={v => upd({ isActive: v })} />
            <Divider sx={{ my: 1.5 }} />
          </>
        )}

        {/* ── Position readout ── */}
        {hotspotType !== 'exhibitor-grid' && (
          <>
            <InspSectionTitle label={`Position (${hotspot.shape})`} />

        {hotspot.shape === 'rect' && hotspot.meta && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            {[
              ['x', hotspot.points?.[0]?.x],
              ['y', hotspot.points?.[0]?.y],
              ['width', hotspot.meta?.width],
              ['height', hotspot.meta?.height],
            ].map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#9ea8b3', width: 44 }}>{k}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#5a6474', fontFamily: 'monospace' }}>{v?.toFixed(1)}%</Typography>
              </Box>
            ))}
          </Box>
        )}

        {hotspot.shape === 'circle' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            {[
              ['cx', hotspot.points?.[0]?.x],
              ['cy', hotspot.points?.[0]?.y],
              ['radius', hotspot.meta?.radius],
            ].map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#9ea8b3', width: 44 }}>{k}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#5a6474', fontFamily: 'monospace' }}>{v?.toFixed(1)}%</Typography>
              </Box>
            ))}
          </Box>
        )}

        {hotspot.shape === 'polygon' && (
          <Typography sx={{ fontSize: '0.7rem', color: '#9ea8b3' }}>
            {hotspot.points?.length || 0} points
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* ── Responsive Position & Size ── */}
        {isRectOrCircle && !isDesktop && (
          <>
            <Box sx={{
              mb: 1, px: 1.25, py: 0.75, borderRadius: 1.5,
              background: alpha(bpColor, 0.07),
              border: `1px dashed ${alpha(bpColor, 0.4)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ color: bpColor, display: 'flex', alignItems: 'center' }}>{bpDef?.icon}</Box>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: bpColor }}>
                  {bpDef?.label} Position Override
                </Typography>
              </Box>
              {hasOverride && (
                <Box onClick={clearBpOverride} sx={{ fontSize: '0.62rem', fontWeight: 600, color: bpColor, cursor: 'pointer', opacity: 0.75, '&:hover': { opacity: 1 } }}>
                  Reset
                </Box>
              )}
            </Box>

            {/* X / Y */}
            <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#9ea8b3', mb: 0.3 }}>X %</Typography>
                <Box component="input"
                  type="number" step="0.1"
                  value={parseFloat(x.toFixed(2))}
                  onChange={e => {
                    const val = parseFloat(e.target.value);
                    if (isNaN(val)) return;
                    const newPoints = [{ ...(effPoints[0] || {}), x: val }];
                    updBpGeometry({ points: newPoints });
                  }}
                  style={{ width: '100%', border: '1.5px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '0.78rem', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb', color: '#1a1a2e' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#9ea8b3', mb: 0.3 }}>Y %</Typography>
                <Box component="input"
                  type="number" step="0.1"
                  value={parseFloat(y.toFixed(2))}
                  onChange={e => {
                    const val = parseFloat(e.target.value);
                    if (isNaN(val)) return;
                    const newPoints = [{ ...(effPoints[0] || {}), y: val }];
                    updBpGeometry({ points: newPoints });
                  }}
                  style={{ width: '100%', border: '1.5px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '0.78rem', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb', color: '#1a1a2e' }}
                />
              </Box>
            </Box>

            {/* W / H (rect) or R (circle) */}
            {hotspot.shape === 'rect' && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1.25 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#9ea8b3', mb: 0.3 }}>W %</Typography>
                  <Box component="input"
                    type="number" step="0.1"
                    value={parseFloat(w.toFixed(2))}
                    onChange={e => {
                      const val = parseFloat(e.target.value);
                      if (isNaN(val)) return;
                      updBpGeometry({ meta: { ...effMeta, width: val } });
                    }}
                    style={{ width: '100%', border: '1.5px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '0.78rem', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb', color: '#1a1a2e' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#9ea8b3', mb: 0.3 }}>H %</Typography>
                  <Box component="input"
                    type="number" step="0.1"
                    value={parseFloat(h.toFixed(2))}
                    onChange={e => {
                      const val = parseFloat(e.target.value);
                      if (isNaN(val)) return;
                      updBpGeometry({ meta: { ...effMeta, height: val } });
                    }}
                    style={{ width: '100%', border: '1.5px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '0.78rem', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb', color: '#1a1a2e' }}
                  />
                </Box>
              </Box>
            )}
            {hotspot.shape === 'circle' && (
              <Box sx={{ mb: 1.25 }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#9ea8b3', mb: 0.3 }}>Radius %</Typography>
                <Box component="input"
                  type="number" step="0.1"
                  value={parseFloat(w.toFixed(2))}
                  onChange={e => {
                    const val = parseFloat(e.target.value);
                    if (isNaN(val)) return;
                    updBpGeometry({ meta: { ...effMeta, radius: val } });
                  }}
                  style={{ width: '100%', border: '1.5px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '0.78rem', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb', color: '#1a1a2e' }}
                />
              </Box>
            )}

            <Divider sx={{ my: 1.5 }} />
          </>
        )}

          </>
        )}

        {/* ── Responsive Visibility + Delete — hidden for exhibitor-grid ── */}
        {hotspotType !== 'exhibitor-grid' && (
          <>
        {/* ── Responsive Visibility ── */}
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <VisibilityOutlined sx={{ fontSize: 13, color: '#9ea8b3' }} />
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Responsive Visibility
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 0.5 }}>
          {LOBBY_BREAKPOINTS.map(bpDef => {
            const isOn = isHotspotVisibleOnBp(hotspot, bpDef.key);
            return (
              <Box
                key={bpDef.key}
                onClick={() => {
                  const current = hotspot.visibleOn || {};
                  // Build full map — default all to true if not set
                  const full = { desktop: true, 'tablet-landscape': true, 'tablet-portrait': true, mobile: true, ...current };
                  upd({ visibleOn: { ...full, [bpDef.key]: !isOn } });
                }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25,
                  px: 0.5, py: 0.6, borderRadius: 1.5, cursor: 'pointer',
                  transition: 'all 0.13s',
                  '&:hover': { background: 'rgba(0,0,0,0.04)' },
                }}
              >
                {/* Checkbox */}
                <Box sx={{
                  width: 15, height: 15, borderRadius: '4px', flexShrink: 0,
                  border: `1.5px solid ${isOn ? bpDef.color : '#c8d0da'}`,
                  background: isOn ? bpDef.color : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.13s',
                }}>
                  {isOn && (
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                      <polyline points="1.5,4.5 3.5,6.5 7.5,2.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </Box>
                {/* Icon */}
                <Box sx={{ color: isOn ? bpDef.color : '#b0bac8', display: 'flex', alignItems: 'center', flexShrink: 0, transition: 'color 0.13s' }}>
                  {React.cloneElement(bpDef.icon, { sx: { fontSize: 14 } })}
                </Box>
                {/* Label */}
                <Typography sx={{ fontSize: '0.72rem', fontWeight: isOn ? 600 : 400, color: isOn ? '#2c3444' : '#9ea8b3', flex: 1, transition: 'all 0.13s' }}>
                  {bpDef.label}
                </Typography>
                {/* Hidden pill */}
                {!isOn && (
                  <Box sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.75, py: 0.2, borderRadius: '4px', background: 'rgba(0,0,0,0.06)', color: '#9ea8b3', letterSpacing: '0.03em' }}>
                    HIDDEN
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* ── Delete ── */}
        <Box
          onClick={onDelete}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            p: 1.25, borderRadius: 2,
            border: '1px solid #fee2e2', background: '#fff5f5',
            cursor: 'pointer', color: '#dc2626', fontSize: '0.78rem', fontWeight: 600,
            '&:hover': { background: '#fee2e2' }, transition: 'all 0.12s',
          }}
        >
          <DeleteOutlineOutlined sx={{ fontSize: 16 }} /> {hotspotType === 'stream' || hotspotType === 'livestream-chat' ? 'Delete element' : hotspotType === 'text' ? 'Delete text' : hotspotType === 'image' ? 'Delete image' : 'Delete hotspot'}
        </Box>
          </>
        )}
      </Box>
    </Box>
  );
}